import { Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useProceduresMutation } from '../queries';
import {
  AddProcedureStockAllowanceType,
  getProcedureInvalidation,
  PostNewProcedure,
  ProcedureType,
} from '../entity';
import { useMemo } from 'react';
import ProcedureCardHeader from './ProcedureCardHeader';
import ProcedureCardActions from './ProcedureCardActions';
import ProcedureCardContent from './ProcedureCardContent';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import AppTheme from '@libs/mui/AppTheme';
import { useSuspenseStocksQuery } from '../../queries';

type ProcedureCardProps = {
  stockAllowances: AddProcedureStockAllowanceType;
  orderNumber: number;
  type?: 'SALE' | 'CONSUMPTION';
  description?: string;
  procedureId?: string;
  disabled?: boolean;
  isPreviousCopy?: boolean;
  procedureRef?: React.RefObject<ImperativeFormDialogRefType | null>;
};

const useProcedureCardData = (
  description: string,
  stockAllowances: AddProcedureStockAllowanceType,
  isPreviousCopy?: boolean,
  type?: 'SALE' | 'CONSUMPTION',
) => {
  const { visitId, clientId } = useParams<{ visitId: string; clientId: string }>();
  const { data: stocks } = useSuspenseStocksQuery();

  const { mutation: createNewProcedure } = useProceduresMutation({
    onSuccess: () => {
      getProcedureInvalidation(stocks, visitId, clientId);
    },
  });

  const cardType: ProcedureType = useMemo(() => {
    if (isPreviousCopy) {
      return 'PREVIOUS_COPY';
    }
    return type || 'CONSUMPTION';
  }, [isPreviousCopy, type]);

  const backgroundColor = useMemo(() => {
    if (cardType === 'PREVIOUS_COPY') return AppTheme.palette.success.light;
    if (cardType === 'SALE') return AppTheme.palette.info.light;
    return AppTheme.palette.common.white;
  }, [cardType]);

  const handleRepeat = () => {
    if (!visitId) {
      return;
    }
    createNewProcedure.mutate({
      description,
      type: 'CONSUMPTION',
      visitId,
      stockAllowances,
    } as unknown as PostNewProcedure);
  };

  return {
    cardType,
    backgroundColor,
    handleRepeat,
    isLoading: createNewProcedure.isPending,
  };
};

const ProcedureCard = ({
  procedureRef,
  description,
  stockAllowances,
  orderNumber,
  type,
  procedureId,
  disabled = false,
  isPreviousCopy = false,
}: ProcedureCardProps) => {
  const { cardType, backgroundColor, handleRepeat, isLoading } = useProcedureCardData(
    description ?? '',
    stockAllowances,
    isPreviousCopy,
    type,
  );

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
      bgcolor={backgroundColor}
      boxShadow="0px 1px 7px 0px rgba(0,0,0,0.22)"
      borderRadius="10px"
    >
      <ProcedureCardHeader
        isPreviousCopy={isPreviousCopy}
        orderNumber={orderNumber}
        cardType={cardType}
      />
      <ProcedureCardContent description={description} stockAllowances={stockAllowances} />
      <ProcedureCardActions
        cardType={cardType}
        description={description}
        stockAllowances={stockAllowances}
        procedureId={procedureId}
        disabled={disabled}
        onRepeat={handleRepeat}
        isLoading={isLoading}
      />
    </Stack>
  );
};

export default ProcedureCard;
